import { Fragment } from 'react'
import type { FinancialPeriod, PnlGroup } from '@/types/entities'
import { sumGroup, noi } from '@/lib/selectors'
import { money, deltaStr, tone } from '@/lib/format'

const GROUP_LABELS: Record<string, string> = {
  revenue: 'Revenue',
  controllable: 'Controllable Expenses',
  uncontrollable: 'Uncontrollable Expenses',
  'below-noi': 'Below NOI',
  capex: 'Capital Expenditures',
}

/** Actual-to-Budget P&L → NOI (spec §8.7). Cost lines: over-budget reads red. */
export function PnLTable({ fp }: { fp: FinancialPeriod }) {
  const groups: PnlGroup[] = ['revenue', 'controllable', 'uncontrollable']
  const noiA = noi(fp, 'actual')
  const noiB = noi(fp, 'budget')

  const Variance = ({ a, b, higherBetter }: { a: number; b: number; higherBetter: boolean }) => {
    const v = a - b
    const t = tone(v, higherBetter)
    return <span className={`pnl-var pnl-var--${t}`}>{deltaStr(v)}</span>
  }

  return (
    <div className="pnl-wrap">
      <table className="pnl">
        <thead>
          <tr>
            <th className="pnl-line-h">Line Item</th>
            <th className="align-right">Actual</th>
            <th className="align-right">Budget</th>
            <th className="align-right">Variance</th>
            <th className="align-right">%</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const lines = fp.lines.filter((l) => l.group === g)
            if (!lines.length) return null
            const subA = sumGroup(fp, g, 'actual')
            const subB = sumGroup(fp, g, 'budget')
            const higherBetter = g === 'revenue'
            const pctVar = subB ? ((subA - subB) / Math.abs(subB)) * 100 : 0
            return (
              <Fragment key={g}>
                <tr className="pnl-group-row"><td colSpan={5}>{GROUP_LABELS[g]}</td></tr>
                {lines.map((l) => {
                  const pv = l.budget ? ((l.actual - l.budget) / Math.abs(l.budget)) * 100 : 0
                  return (
                    <tr key={l.key} className="pnl-line">
                      <td className="pnl-line-label">{l.label}</td>
                      <td className="align-right num">{money(l.actual)}</td>
                      <td className="align-right num">{money(l.budget)}</td>
                      <td className="align-right"><Variance a={l.actual} b={l.budget} higherBetter={higherBetter} /></td>
                      <td className="align-right num pnl-pct">{l.budget ? `${pv > 0 ? '+' : ''}${pv.toFixed(1)}%` : '—'}</td>
                    </tr>
                  )
                })}
                <tr className="pnl-subtotal">
                  <td>Total {GROUP_LABELS[g]}</td>
                  <td className="align-right num">{money(subA)}</td>
                  <td className="align-right num">{money(subB)}</td>
                  <td className="align-right"><Variance a={subA} b={subB} higherBetter={higherBetter} /></td>
                  <td className="align-right num pnl-pct">{`${pctVar > 0 ? '+' : ''}${pctVar.toFixed(1)}%`}</td>
                </tr>
              </Fragment>
            )
          })}
          <tr className="pnl-noi">
            <td>Net Operating Income</td>
            <td className="align-right num">{money(noiA)}</td>
            <td className="align-right num">{money(noiB)}</td>
            <td className="align-right"><Variance a={noiA} b={noiB} higherBetter /></td>
            <td className="align-right num pnl-pct">{noiB ? `${noiA - noiB > 0 ? '+' : ''}${(((noiA - noiB) / Math.abs(noiB)) * 100).toFixed(1)}%` : '—'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
